import type { Tune } from "./tune.js";

/**
 * Engine and automatic gearbox (pure, unit tested). The engine turns with the driven wheels
 * through the gear (a slipping clutch keeps it above idle when pulling away); torque follows a
 * curve that rises to its peak and falls toward the redline; the limiter cuts it. The gearbox
 * shifts up near the redline and down when the revs drop, cutting the drive for `shiftTime`.
 */

export interface Gearbox {
  /** -1 reverse, 0 neutral, 1..n. */
  gear: number;
  rpm: number;
  /** Seconds left of a shift in progress. */
  shifting: number;
  /** Counts every shift (the audio plays a shift when it changes). */
  shifts: number;
}

export const newGearbox = (t: Tune): Gearbox => ({
  gear: 1,
  rpm: t.idleRpm,
  shifting: 0,
  shifts: 0,
});

/** Share (0..1) of the peak torque the engine makes at `rpm` with the throttle open. */
export function torqueCurve(t: Tune, rpm: number): number {
  if (rpm >= t.limiter) return 0;
  if (rpm <= t.peakRpm) {
    const x = Math.max(0, rpm - t.idleRpm) / Math.max(1, t.peakRpm - t.idleRpm);
    return 0.62 + 0.38 * Math.sin((x * Math.PI) / 2);
  }
  const x = (rpm - t.peakRpm) / Math.max(1, t.redline - t.peakRpm);
  return Math.max(0.2, 1 - 0.32 * Math.pow(Math.min(x, 1.3), 1.6));
}

/** Overall ratio of a gear (engine turns per wheel turn). */
export const ratioOf = (t: Tune, gear: number): number =>
  gear < 0 ? t.reverse * t.final : gear === 0 ? 0 : (t.gears[gear - 1] ?? 1) * t.final;

/** Engine speed for a wheel speed (rad/s) in a gear, with the launch clutch above idle. */
export function engineRpm(t: Tune, wheelOmega: number, gear: number, throttle: number): number {
  const ratio = ratioOf(t, gear);
  const fromWheels = (Math.abs(wheelOmega) * ratio * 60) / (Math.PI * 2);
  // A slipping clutch: pulling away, the engine sits above idle in proportion to the throttle.
  const launch = t.idleRpm + throttle * (t.peakRpm - t.idleRpm) * 0.55;
  return Math.min(
    t.limiter,
    Math.max(gear === 0 ? t.idleRpm + throttle * 3000 : launch, fromWheels),
  );
}

export interface DriveOut {
  /** Total drive force at the contact patches (N), signed along the car's heading. */
  force: number;
  /** Engine braking force (N, >= 0, against the motion). */
  engineBrake: number;
  box: Gearbox;
}

/**
 * One step of the powertrain: shifts, revs and the drive force. `speed` is the car's forward
 * speed (m/s), `reverse` asks for reverse (the brake pedal held at a standstill).
 */
export function stepPowertrain(
  t: Tune,
  box: Gearbox,
  speed: number,
  throttle: number,
  reverse: boolean,
  wheelRadius: number,
  dt: number,
): DriveOut {
  let { gear, shifting, shifts } = box;
  const n = t.gears.length;
  // Reverse and back: only near a standstill.
  if (reverse && gear > 0 && speed < 1) {
    gear = -1;
    shifts++;
    shifting = t.shiftTime;
  } else if (!reverse && gear < 0 && speed > -1 && throttle > 0) {
    gear = 1;
    shifts++;
    shifting = t.shiftTime;
  } else if (gear === 0) gear = 1;
  const omega = speed / wheelRadius;
  let rpm = engineRpm(t, omega, gear, throttle);
  if (gear > 0 && shifting <= 0) {
    const up = t.redline - (t.redline - t.peakRpm) * 0.18 * (1 - throttle);
    const down = t.peakRpm * 0.55 + (t.peakRpm - t.idleRpm) * 0.25 * throttle;
    if (rpm > up && gear < n && throttle > 0.05) {
      gear++;
      shifts++;
      shifting = t.shiftTime;
    } else if (gear > 1) {
      const below = engineRpm(t, omega, gear - 1, throttle);
      if (rpm < down && below < up * 0.92) {
        gear--;
        shifts++;
        shifting = t.shiftTime * 0.7;
      }
    }
    rpm = engineRpm(t, omega, gear, throttle);
  }
  shifting = Math.max(0, shifting - dt);
  // The needle eases (no jumps on a shift).
  const shown = box.rpm + (rpm - box.rpm) * Math.min(1, dt * 14);
  const cut = shifting > 0 ? 0.15 : 1;
  const engineTorque = t.torqueNm * torqueCurve(t, rpm) * throttle * cut;
  const ratio = ratioOf(t, gear);
  const sign = gear < 0 ? -1 : 1;
  const force = (sign * engineTorque * ratio * t.efficiency) / wheelRadius;
  // Engine braking off the throttle, stronger in low gears and at high revs.
  const brakeTorque = t.torqueNm * 0.16 * (rpm / t.redline) * (1 - throttle);
  const engineBrake = gear !== 0 ? (brakeTorque * ratio) / wheelRadius : 0;
  return { force, engineBrake, box: { gear, rpm: shown, shifting, shifts } };
}
